import React, { useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

export const QCDataDev3 = ({
  records,
  editMode,
  editableTitles,
  handleTitleChange,
  handleEditTitle,
  setEditMode,
  cycleMasterId,
  verifyTitle,
  verifyAllTitles,
  statusInfo,
  productsData,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(() => () => {});

  const handleEditClick = (uniqueKey) => {
    setEditMode((prev) => ({ ...prev, [uniqueKey]: true }));
  };

  const formatQcStatus = (status) => {
    if (!status || status === "none") return "Not verified";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const product = productsData[records.productId];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-2 mb-1 transition">
      {/* Product Header */}
      <div className="flex items-center mb-1 border-b border-gray-200 dark:border-gray-700 pb-1">
        <div className="relative w-16 h-16 rounded-full overflow-hidden border border-gray-300 shadow-sm">
          <img
            src={product?.product_image_url || "/feedsumo_2/no_image.png"}
            alt={product?.product_name}
            className="object-cover w-full h-full"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/feedsumo-new/no_image.png";
            }}
          />
        </div>
        <div className="ml-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {product?.product_name}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Product ID: {records.productId}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {records.qc_status === "verified" ? (
            <button
              disabled={statusInfo.cycle_status === "started"}
              onClick={() =>
                verifyAllTitles(cycleMasterId, records.productId, "not verified")
              }
              className="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 text-white rounded-md disabled:bg-gray-300"
            >
              Unverify All
            </button>
          ) : (
            <button
              disabled={statusInfo.cycle_status === "started"}
              onClick={() =>
                verifyAllTitles(cycleMasterId, records.productId, "verified")
              }
              className="px-3 py-1 text-sm bg-green-500 hover:bg-green-600 text-white rounded-md disabled:bg-gray-300"
            >
              Verify All
            </button>
          )}
        </div>
      </div>

      {/* Title List */}
      <div className="space-y-1">
        {Array.isArray(records?.title) &&
          records.title.map((item, idx) => {
            const uniqueKey = `${records.productId}_${idx}`;
            const isEditing = editMode[uniqueKey];
            const qcStatus = formatQcStatus(item.qc_status);
            const isVerified = qcStatus === "Verified";

            return (
              <div
                key={uniqueKey}
                className="flex flex-col sm:flex-row sm:items-center justify-between bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-2 hover:shadow-sm transition"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {item.key === "start"
                      ? "Baseline Title"
                      : item.key.charAt(0).toUpperCase() + item.key.slice(1)}
                  </h3>
                  {!isEditing ? (
                    <p className="text-gray-800 dark:text-gray-300 mt-1 break-words">
                      {item.title}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={editableTitles[uniqueKey] || item.title}
                      onChange={(e) => handleTitleChange(e, uniqueKey)}
                      className="mt-1 border border-gray-300 rounded-md px-3 py-1 w-full focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    />
                  )}
                </div>

                {item.key !== "start" && (
                  <div className="flex items-center gap-3 mt-2 sm:mt-0 sm:ml-4">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        isVerified
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {qcStatus}
                    </span>

                    {!isEditing ? (
                      <>
                        <button
                          disabled={statusInfo.cycle_status === "started"}
                          onClick={() => handleEditClick(uniqueKey)}
                          className="text-blue-600 hover:underline text-sm disabled:text-gray-400"
                        >
                          Edit
                        </button>
                        <button
                          disabled={statusInfo.cycle_status === "started"}
                          onClick={() =>
                            verifyTitle(
                              cycleMasterId,
                              records.productId,
                              idx,
                              uniqueKey,
                              item.key,
                              isVerified ? "not verified" : "verified"
                            )
                          }
                          className={`text-sm ${
                            isVerified
                              ? "text-red-600 hover:underline"
                              : "text-green-600 hover:underline"
                          } disabled:text-gray-400`}
                        >
                          {isVerified ? "Unverify" : "Verify"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          disabled={
                            !editableTitles[uniqueKey] ||
                            editableTitles[uniqueKey].trim() ===
                              item.title.trim()
                          }
                          onClick={() =>
                            handleEditTitle(
                              cycleMasterId,
                              records.productId,
                              idx,
                              uniqueKey,
                              editableTitles[uniqueKey],
                              item.key,
                              item.title
                            )
                          }
                          className="text-green-600 text-sm hover:underline disabled:text-gray-400"
                        >
                          Save
                        </button>
                        <button
                          onClick={() =>
                            setEditMode((prev) => ({
                              ...prev,
                              [uniqueKey]: false,
                            }))
                          }
                          className="text-red-500 text-sm hover:underline"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          confirmAction();
          setConfirmOpen(false);
        }}
        message={confirmMessage}
      />
    </div>
  );
};
